import { AxiosResponse } from 'axios';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useMutation, UseMutationResult } from 'react-query';
import { backend, LoginResponse, loginUser, refreshAuthToken } from '../axios/backend';
import Toast from '../components/ui/Toast';
import { parseErrorMessage } from '../utils/error-message';
import { AuthState, initialAuthState, PreUserType, useAuthReducer, UserType } from './reducers/useAuthReducer';

const LOCAL_STORAGE_NAME = 'mt-bringsl-auth';
const LOCAL_STORAGE_AUTH_USER = `${LOCAL_STORAGE_NAME}-user`;
export const LOCAL_STORAGE_AUTH_TOKEN = `${LOCAL_STORAGE_NAME}-token`;
const LOCAL_STORAGE_AUTH_TOKEN_EXP = `${LOCAL_STORAGE_AUTH_TOKEN}-exp`;
const LOCAL_STORAGE_REFRESH_TOKEN = `${LOCAL_STORAGE_NAME}-refresh-token`;

const ERROR_RESET_TIME = 6000;
const SUCCESS_RESET_TIME = 2000;

type ContextType = {
  authState: AuthState;
  isAuthenticated: boolean;
  user: UserType;
  login: (username: string, password: string) => void;
  logout: () => void;
  alertError: (message: string) => void;
  alertSuccess: (message: string) => void;
  preUserMutation: UseMutationResult<AxiosResponse<string, any>, unknown, { name: string }, unknown> | null;
  preUser: PreUserType | undefined;
  verifyUser: () => void;
};

export const Context = createContext<ContextType>({
  authState: initialAuthState,
  login: () => {},
  logout: () => {},
  alertError: () => {},
  alertSuccess: () => {},
  isAuthenticated: initialAuthState.isAuthenticated,
  user: initialAuthState.user,
  preUserMutation: null,
  preUser: undefined,
  verifyUser: () => {},
});

const AppContext: React.FunctionComponent = (props) => {
  const [authState, authDispatch] = useAuthReducer();
  const [preUser, setPreUser] = useState<PreUserType>();

  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [successMessage, setSuccessMessage] = useState<string | undefined>();

  useEffect(() => {
    checkTokenExpiry();

    const interval = setInterval(() => checkTokenExpiry(), 10 * 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const preUserMutation = useMutation(
    (options: { name: string }) => backend.post<string>(`/auth/register-pre-user`, options),
    {
      onSuccess: (response, variables) => {
        const preUser: PreUserType = { id: response.data, name: variables.name };
        setPreUser(preUser);
        localStorage.setItem('pre-user', JSON.stringify(preUser));
      },
    },
  );

  useEffect(() => {
    const rawPreUser = localStorage.getItem('pre-user');
    if (rawPreUser) {
      try {
        const preUser: PreUserType = JSON.parse(rawPreUser);
        // Make sure the schema is all right
        // To-Do: move somewhere more central
        if (preUser.id && preUser.name) setPreUser(preUser);
      } catch (e) {
        console.error('Error parsing pre user!');
      }
    }
  }, []);

  const setLoggedInUser = (loginResponse: LoginResponse) => {
    localStorage.setItem(LOCAL_STORAGE_AUTH_USER, JSON.stringify(loginResponse.user));
    localStorage.setItem(LOCAL_STORAGE_AUTH_TOKEN, loginResponse.authToken);
    localStorage.setItem(LOCAL_STORAGE_AUTH_TOKEN_EXP, String(loginResponse.authTokenExp));
    localStorage.setItem(LOCAL_STORAGE_REFRESH_TOKEN, loginResponse.refreshToken);

    authDispatch({ type: 'login', user: loginResponse.user });
  };

  const login = (username: string, password: string) => {
    authDispatch({ type: 'login-load' });

    loginUser(username, password)
      .then((loginResponse) => {
        setLoggedInUser(loginResponse);
      })
      .catch((error) => {
        const errorMessage = parseErrorMessage(error);
        authDispatch({ type: 'login-error', errorMessage });
      });
  };

  const refresh = async (refreshToken: string) => {
    try {
      const loginResponse = await refreshAuthToken(refreshToken);
      setLoggedInUser(loginResponse);
    } catch (e) {
      console.error('Could not refresh token. Logging out now...');
      logout();
    }
  };

  const logout = () => {
    localStorage.removeItem(LOCAL_STORAGE_AUTH_USER);
    localStorage.removeItem(LOCAL_STORAGE_AUTH_TOKEN);
    localStorage.removeItem(LOCAL_STORAGE_AUTH_TOKEN_EXP);
    localStorage.removeItem(LOCAL_STORAGE_REFRESH_TOKEN);
    authDispatch({ type: 'logout' });
  };

  const verifyUser = () => {
    authDispatch({ type: 'verify' });
    localStorage.setItem(LOCAL_STORAGE_AUTH_USER, JSON.stringify({ ...authState.user, verified: true }));
  };

  const checkTokenExpiry = () => {
    const authToken = localStorage.getItem(LOCAL_STORAGE_AUTH_TOKEN);
    const authTokenExp = parseInt(localStorage.getItem(LOCAL_STORAGE_AUTH_TOKEN_EXP) || '0');
    const refreshToken = localStorage.getItem(LOCAL_STORAGE_REFRESH_TOKEN);

    const timeStamp = Date.now();

    // If auth token expires in next minute
    if (authTokenExp - 1000 * 60 < timeStamp) {
      // Try to refresh the auth token
      if (refreshToken) {
        refresh(refreshToken);
      } else {
        logout();
      }
    } else if (!authToken) {
      logout();
    } else if (!authState.isAuthenticated) {
      try {
        const userString = localStorage.getItem(LOCAL_STORAGE_AUTH_USER);
        if (!userString) throw new Error('No User');
        const user: UserType = JSON.parse(userString);

        authDispatch({ type: 'login', user });
      } catch (e) {
        logout();
      }
    }
  };

  return (
    <Context.Provider
      value={{
        authState,
        login,
        logout,
        alertError: (errorMessage: string) => {
          setErrorMessage(errorMessage);
          setTimeout(() => setErrorMessage(undefined), ERROR_RESET_TIME);
        },
        alertSuccess: (successMessage: string) => {
          setSuccessMessage(successMessage);
          setTimeout(() => setSuccessMessage(undefined), SUCCESS_RESET_TIME);
        },
        isAuthenticated: authState.isAuthenticated,
        user: authState.user,
        preUser,
        preUserMutation,
        verifyUser,
      }}
    >
      {errorMessage && <Toast variant="danger">{errorMessage}</Toast>}
      {successMessage && <Toast variant="success">{successMessage}</Toast>}
      {props.children}
    </Context.Provider>
  );
};

export const useAppContext = () => useContext(Context);

export default AppContext;
